import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import EventsCalendarWidget from "@/components/EventsCalendarWidget";
import { getEvents } from "@/lib/api";
import { MapPin, Clock, ExternalLink, Calendar } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "short",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function EventsPage() {
    const [events, setEvents] = useState([]);

    useEffect(() => {
        getEvents()
            .then((list) => {
                const today = new Date().toISOString().slice(0, 10);
                const sorted = [...list].sort((a, b) =>
                    a.date.localeCompare(b.date),
                );
                const upcoming = sorted.filter((e) => e.date >= today);
                const past = sorted.filter((e) => e.date < today).reverse();
                setEvents([...upcoming, ...past]);
            })
            .catch(() => setEvents([]));
    }, []);

    const today = new Date().toISOString().slice(0, 10);

    return (
        <div data-testid="events-page">
            <PageHero
                eyebrow="Calendar"
                title="Events"
                lead="Dinners, festivals, talks, launches and pop-ups across food & hospitality. Pick a date — or scroll the full list."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Events" },
                ]}
            />

            <div className="px-4 md:px-8 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8 space-y-4">
                    {events.length === 0 ? (
                        <p className="font-sofia text-[#1B1B1B]/60">
                            No events scheduled.
                        </p>
                    ) : (
                        events.map((e) => {
                            const isPast = e.date < today;
                            return (
                                <article
                                    key={e.id}
                                    className={`flex items-stretch gap-5 border-l-4 ${
                                        isPast
                                            ? "border-black/15 opacity-70"
                                            : "border-[#E63B26]"
                                    } bg-white p-5 transition hover:shadow-md`}
                                    data-testid={`events-list-item-${e.id}`}
                                >
                                    <div
                                        className={`flex flex-col items-center justify-center min-w-[88px] text-center px-3 py-2 font-poppins ${
                                            isPast
                                                ? "bg-[#1B1B1B]/80 text-white/80"
                                                : "bg-[#F5C53D] text-[#1B1B1B]"
                                        }`}
                                    >
                                        <span className="text-xs tracking-[0.18em] uppercase">
                                            {new Date(e.date).toLocaleDateString(undefined, {
                                                month: "short",
                                            })}
                                        </span>
                                        <span className="font-black text-3xl leading-none">
                                            {new Date(e.date).getDate()}
                                        </span>
                                        <span className="text-[10px] tracking-widest">
                                            {new Date(e.date).getFullYear()}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-poppins font-bold text-lg md:text-xl text-[#1B1B1B]">
                                            {e.title}
                                        </h3>
                                        <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar className="h-3.5 w-3.5" />
                                                {formatDate(e.date)}
                                            </span>
                                            {e.time && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {e.time}
                                                </span>
                                            )}
                                            {e.location && (
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin className="h-3.5 w-3.5" />
                                                    {e.location}
                                                </span>
                                            )}
                                        </p>
                                        {e.description && (
                                            <p className="font-sofia text-[#1B1B1B]/85 mt-2 leading-relaxed">
                                                {e.description}
                                            </p>
                                        )}
                                        {e.link && (
                                            <a
                                                href={e.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-2 inline-flex items-center gap-1 text-[#E63B26] font-poppins text-xs tracking-[0.18em] uppercase hover:underline"
                                            >
                                                Details <ExternalLink className="h-3 w-3" />
                                            </a>
                                        )}
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>

                <div className="lg:col-span-4">
                    <EventsCalendarWidget />
                </div>
            </div>
        </div>
    );
}
